import { TICKET_AUDIT_ACTION_LABELS, type TicketAuditAction } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { Link, useParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTicket, useTicketHistory } from '@/products/helpdesk/features/tickets/use-tickets';
import { useAppPaths } from '@/products/helpdesk/features/portal/portal-paths';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

export function TicketHistoryPage() {
  const { ticketRef } = useParams<{ ticketRef: string }>();
  const ticket = useTicket(ticketRef);
  const history = useTicketHistory(ticketRef);
  const paths = useAppPaths();

  if (ticket.isPending || history.isPending) {
    return (
      <p className="flex items-center gap-2 text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Historie wird geladen …
      </p>
    );
  }

  if (ticket.isError || !ticket.data) {
    return (
      <section className="space-y-3">
        <h1 className="text-2xl font-bold">
          {paths.isPortal ? 'Anfrage nicht gefunden' : 'Ticket nicht gefunden'}
        </h1>
        <p className="text-muted-foreground">
          {ticket.error instanceof ApiRequestError
            ? ticket.error.message
            : 'Das Ticket konnte nicht geladen werden.'}
        </p>
        <Button asChild variant="outline">
          <Link to={paths.tickets}>
            {paths.isPortal ? 'Zurück zu meinen Anfragen' : 'Zurück zur Übersicht'}
          </Link>
        </Button>
      </section>
    );
  }

  const items = history.data?.items ?? [];

  return (
    <section className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-1 -ml-2">
          <Link to={paths.ticket(ticket.data.number)}>
            ← Zurück{' '}
            {paths.isPortal
              ? `zur Anfrage #${ticket.data.number}`
              : `zu Ticket #${ticket.data.number}`}
          </Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">
          {paths.isPortal ? 'Alle Schritte' : 'Ticket-Historie'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{ticket.data.subject}</p>
      </div>

      {history.isError && (
        <p role="alert" className="text-sm text-destructive">
          {history.error instanceof ApiRequestError
            ? history.error.message
            : 'Die Historie konnte nicht geladen werden.'}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{paths.isPortal ? 'Was sich geändert hat' : 'Audit-Log'}</CardTitle>
          <CardDescription>
            {paths.isPortal
              ? 'Die wichtigsten Stationen dieser Anfrage, neueste zuerst.'
              : 'Alle nachvollziehbaren Änderungen an diesem Ticket, neueste Einträge zuerst.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 sm:px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zeitpunkt</TableHead>
                <TableHead>Aktion</TableHead>
                <TableHead className="hidden sm:table-cell">Ausgeführt von</TableHead>
                <TableHead>Beschreibung</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                    Noch keine Historie-Einträge.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="whitespace-nowrap">
                      {formatDateTime(item.createdAt)}
                    </TableCell>
                    <TableCell>{actionLabel(item.action)}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {item.actor?.displayName ?? 'System'}
                    </TableCell>
                    <TableCell className="whitespace-pre-wrap">{item.summary}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </section>
  );
}

function actionLabel(action: string): string {
  if (action in TICKET_AUDIT_ACTION_LABELS) {
    return TICKET_AUDIT_ACTION_LABELS[action as TicketAuditAction];
  }
  return action;
}
